import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Clock, MapPin, Check, Target, Lock, Pencil, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import GlassCard from "./GlassCard";
import TeamIcon from "@/components/TeamIcon";
import { colors } from "../constants";

import { BACKEND_URL } from "@/lib/api";
import { PREDICTION_WINDOW_MS, PREDICTION_MIN, PREDICTION_MAX } from "@/constants";

// Prediction Timeline Component - Shows countdown to match start
const PredictionTimeline = ({ matchDate: matchDateISO, isLive, isLocked, isNotYetOpen }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const updateCountdown = () => {
      if (!matchDateISO) return;
      const matchDate = new Date(matchDateISO);
      if (isNaN(matchDate.getTime())) return;

      const now = new Date();
      const diff = matchDate.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft(null);
        setProgress(100);
        return;
      }

      const totalMinutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const days = Math.floor(hours / 24);

      if (days > 0) {
        setTimeLeft(`${days}d ${hours % 24}h`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m`);
      }

      // Calculate progress (assume 8 hour window for predictions)
      const totalWindow = PREDICTION_WINDOW_MS; // 8 hours in ms
      const elapsed = totalWindow - Math.min(diff, totalWindow);
      setProgress(Math.min((elapsed / totalWindow) * 100, 100));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [matchDateISO]);

  if (isLive) {
    return (
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-center gap-2">
          <Lock className="w-3 h-3 text-white/50" />
          <span className="text-xs text-white/50">Prediction locked</span>
        </div>
      </div>
    );
  }

  const displayTime = matchDateISO
    ? new Date(matchDateISO).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "TBD";

  return (
    <div className="mt-4 pt-4 border-t border-white/10">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-white/50">
          {isNotYetOpen
            ? "Predictions open 8 hours before match"
            : "Predictions close at match start"}
        </span>
        {timeLeft && !isNotYetOpen && (
          <span className="text-xs font-medium" style={{ color: colors.accent }}>
            Closes in {timeLeft}
          </span>
        )}
        {timeLeft && isNotYetOpen && (
          <span className="text-xs font-medium text-white/40">Starts in {timeLeft}</span>
        )}
      </div>
      <div
        className="w-full h-1.5 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.08)" }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${progress}%`,
            background: "linear-gradient(90deg, #01ff9a, #acffe5)",
          }}
        />
      </div>
    </div>
  );
};

// Status Chip Component
const StatusChip = ({ status, children }) => {
  const statusStyles = {
    live: {
      bg: "rgba(239, 68, 68, 0.15)",
      border: "rgba(239, 68, 68, 0.3)",
      text: "#ef4444",
      dot: "#ef4444",
    },
    closed: {
      bg: "rgba(255,255,255,0.06)",
      border: "rgba(255,255,255,0.1)",
      text: "rgba(255,255,255,0.5)",
    },
    open: {
      bg: "rgba(1, 255, 154, 0.1)",
      border: "rgba(1, 255, 154, 0.3)",
      text: "#01ff9a",
    },
    locked: {
      bg: "rgba(255,255,255,0.06)",
      border: "rgba(255,255,255,0.1)",
      text: "rgba(255,255,255,0.5)",
    },
    winner: {
      bg: "rgba(1, 255, 154, 0.15)",
      border: "rgba(1, 255, 154, 0.3)",
      text: "#01ff9a",
    },
  };
  const style = statusStyles[status] || statusStyles.closed;

  return (
    <div
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        color: style.text,
      }}
    >
      {status === "live" && (
        <span
          className="w-1.5 h-1.5 rounded-full animate-pulse"
          style={{ background: style.dot }}
        />
      )}
      {children}
    </div>
  );
};

// Accent Text (Gradient)
const AccentText = ({ children, className = "" }) => (
  <span
    className={`bg-clip-text text-transparent bg-gradient-to-r from-[#01ff9a] to-[#acffe5] ${className}`}
  >
    {children}
  </span>
);

// Match Card Component (Premium Layout)
const MatchCard = ({
  match,
  userPrediction,
  canPredict,
  user,
  onPredictionSubmit,
  isLiveMatch,
  showTimeline = false,
  matchNumber = 1,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sliderValue, setSliderValue] = useState(userPrediction || 300);
  const [submitting, setSubmitting] = useState(false);
  const [justLocked, setJustLocked] = useState(false);
  const [localPrediction, setLocalPrediction] = useState(userPrediction);
  const auth = useAuth();
  const navigate = useNavigate();

  // Update local prediction when prop changes
  useEffect(() => {
    setLocalPrediction(userPrediction);
    if (userPrediction) {
      setSliderValue(userPrediction);
    }
  }, [userPrediction]);

  const isLive = match.status === "live" || isLiveMatch;
  const isCompleted = match.status === "completed";
  const isUpcoming = match.status === "upcoming" || match.starts_soon;
  const canMakePrediction = canPredict && !isLive && !isCompleted;

  // Check if predictions haven't opened yet (more than 8 hours before match)
  const isPredictionNotYetOpen = (() => {
    if (isLive || isCompleted || canMakePrediction) return false;
    const closesAt = match.prediction_closes_at || match.match_date;
    if (!closesAt) return false;
    const closes = new Date(closesAt);
    if (isNaN(closes.getTime())) return false;
    const opens = new Date(closes.getTime() - PREDICTION_WINDOW_MS);
    return new Date() < opens;
  })();

  const getTotalRuns = () => (match.team_a_score || 0) + (match.team_b_score || 0);

  // Extract city from venue (e.g., "Wankhede Stadium, Mumbai" -> "Mumbai")
  const getCity = () => {
    const venue = match.venue || "";
    const parts = venue.split(",");
    return parts.length > 1 ? parts[parts.length - 1].trim() : parts[0].trim() || match.team_a;
  };

  const handleCardClick = () => {
    if (isLive) {
      navigate(`/match/${match.id}`);
      return;
    }
    if ((isUpcoming || match.starts_soon) && canMakePrediction && !localPrediction) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleSubmitPrediction = async (e) => {
    e.stopPropagation();
    setSubmitting(true);
    try {
      const fetchFn = auth?.authFetch || fetch;
      const response = await fetchFn(`${BACKEND_URL}/api/predictions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          match_id: match.id,
          predicted_runs: sliderValue,
        }),
      });
      if (response.ok) {
        // Update local state immediately
        setLocalPrediction(sliderValue);
        setIsExpanded(false);
        setJustLocked(true);

        // Persist to localStorage so prediction survives page reload
        try {
          const userId = user?.user_id || user?.id;
          const key = `hpl_pred_${userId}_${match.id}`;
          localStorage.setItem(key, String(sliderValue));
        } catch (_) {}

        // Show success toast
        toast.success(localPrediction ? "Prediction updated!" : "Prediction locked! 🎯");

        // Clear animation after delay
        setTimeout(() => setJustLocked(false), 2000);

        // Callback to parent
        onPredictionSubmit && onPredictionSubmit(match.id, sliderValue);
      } else {
        toast.error("Failed. Try again.");
      }
    } catch (error) {
      toast.error("Failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={handleCardClick}
      className={`cursor-pointer ${(canMakePrediction || isLive) ? "hover:scale-[1.01] transition-transform duration-200" : ""}`}
    >
      <GlassCard padding="p-[22px]">
        {/* Match Metadata Row - New format: M1 • 📍 Mumbai */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[13px]" style={{ color: colors.textMuted }}>
            <span className="font-medium">M{matchNumber}</span>
            <span>•</span>
            <MapPin className="w-3 h-3" />
            <span>{getCity()}</span>
          </div>
          {isLive && (
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ background: "#ef4444", boxShadow: "0 0 8px #ef4444" }}
              />
              <span className="text-xs font-semibold text-[#ef4444]">LIVE</span>
              <ChevronRight className="w-3.5 h-3.5 text-white/40" />
            </div>
          )}
          {match.starts_soon && !isLive && <StatusChip status="open">Starting Soon</StatusChip>}
        </div>

        {/* Teams Row - mb-18px */}
        <div className="flex items-center justify-between gap-2 mb-[18px]">
          {/* Team A */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <TeamIcon team={match.team_a} size="sm" />
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate max-w-[80px]">{match.team_a}</p>
              {(isLive || isCompleted) && match.team_a_score !== null && (
                <p className="text-[26px] font-bold text-white leading-tight">
                  {match.team_a_score}
                  <span className="text-sm font-normal text-white/40">
                    /{match.team_a_wickets || 0}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* VS */}
          <div className="px-1 flex-shrink-0">
            <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
              VS
            </span>
          </div>

          {/* Team B */}
          <div className="flex items-center gap-2 flex-1 justify-end text-right min-w-0">
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate max-w-[80px] ml-auto">
                {match.team_b}
              </p>
              {(isLive || isCompleted) && match.team_b_score !== null && (
                <p className="text-[26px] font-bold text-white leading-tight">
                  {match.team_b_score}
                  <span className="text-sm font-normal text-white/40">
                    /{match.team_b_wickets || 0}
                  </span>
                </p>
              )}
            </div>
            <TeamIcon team={match.team_b} size="sm" />
          </div>
        </div>

        {/* Match Score Summary Row - shown for live matches or when scores exist */}
        {(isLive || (!canMakePrediction && (match.total_runs > 0 || getTotalRuns() > 0))) && (
          <div
            className="flex items-center justify-between p-4 rounded-xl mb-[14px]"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {/* Total Match Score - Left */}
            <div className="text-center">
              <p className="text-[10px] font-medium mb-1" style={{ color: colors.textMuted }}>
                {isLive ? "Total Match Score" : "Total Runs"}
              </p>
              <div className="flex items-center gap-1.5">
                {isLive && (
                  <span
                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{ background: colors.accent }}
                  />
                )}
                <span
                  className="text-2xl font-bold"
                  style={{
                    background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {match.total_runs || getTotalRuns()}
                </span>
              </div>
              {isLive && match.current_over != null && (
                <p className="text-[10px] font-medium mt-1" style={{ color: colors.textMuted }}>
                  Ov {match.current_over}
                </p>
              )}
            </div>

            {/* Your Prediction - Right */}
            <div className="text-center">
              <p className="text-[10px] font-medium mb-1" style={{ color: colors.textMuted }}>
                Your Prediction
              </p>
              <span
                className="text-2xl font-bold"
                style={{
                  color: localPrediction || userPrediction ? "#ffffff" : "rgba(255,255,255,0.3)",
                }}
              >
                {localPrediction || userPrediction || "—"}
              </span>
            </div>
          </div>
        )}

        {/* Prediction Interaction Box */}
        {!isLive && !isExpanded && (
          <motion.div
            className="mt-4 p-4 rounded-xl"
            style={{
              background: localPrediction ? "rgba(1, 255, 154, 0.08)" : "rgba(1, 255, 154, 0.05)",
              border: `1px solid ${justLocked ? "rgba(1, 255, 154, 0.5)" : "rgba(1, 255, 154, 0.15)"}`,
              boxShadow: justLocked ? "0 0 20px rgba(1, 255, 154, 0.2)" : "none",
            }}
            animate={justLocked ? { scale: [1, 1.02, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            {localPrediction ? (
              /* After User Sets Prediction - Show prediction with Edit icon */
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>
                    Your Prediction
                  </p>
                  <div className="flex items-center gap-2">
                    <motion.p
                      className="text-xl font-bold"
                      style={{ color: colors.accent }}
                      animate={justLocked ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 0.3 }}
                    >
                      {localPrediction} runs
                    </motion.p>
                    {justLocked && (
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ background: colors.accent }}
                      >
                        <Check className="w-3 h-3 text-black" />
                      </motion.div>
                    )}
                  </div>
                </div>
                {canMakePrediction ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSliderValue(localPrediction);
                      setIsExpanded(true);
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-80"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                ) : (
                  <div
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      color: "rgba(255,255,255,0.3)",
                    }}
                  >
                    <Lock className="w-3 h-3" />
                    Locked
                  </div>
                )}
              </div>
            ) : canMakePrediction ? (
              /* Before Prediction - Initial State */
              <div
                className="text-center cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
              >
                <p className="text-xs mb-3" style={{ color: "rgba(255,255,255,0.6)" }}>
                  Predict the overall runs scored by both teams.
                </p>
                <div
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-90 hover:scale-[1.02]"
                  style={{
                    background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                    color: "#0f0f0f",
                  }}
                >
                  <Target className="w-4 h-4" />
                  Make Prediction
                </div>
              </div>
            ) : (
              /* Predictions Closed or Not Yet Open */
              <div className="text-center">
                <div
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    color: "rgba(255,255,255,0.4)",
                  }}
                >
                  {isPredictionNotYetOpen ? (
                    <>
                      <Clock className="w-4 h-4" />
                      Opens 8h before match
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Predictions Closed
                    </>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Prediction Timeline (Today matches only) */}
        {showTimeline && !isExpanded && !isLive && (
          <PredictionTimeline
            matchDate={match.date || match.match_date || match.prediction_closes_at}
            isLive={isLive}
            isLocked={!canMakePrediction}
            isNotYetOpen={isPredictionNotYetOpen}
          />
        )}

        {/* Expanded Prediction Slider */}
        <AnimatePresence>
          {isExpanded && canMakePrediction && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mt-5 pt-5 border-t border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-2">
                <p className="text-xs text-white/50 mb-4">
                  Predict the overall runs scored by both teams.
                </p>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSliderValue((v) => Math.max(PREDICTION_MIN, v - 1));
                    }}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold transition-all active:scale-90"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.6)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    −
                  </button>
                  <motion.span
                    className="text-5xl font-bold min-w-[80px] text-center"
                    style={{ color: colors.accent }}
                    key={sliderValue}
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.1 }}
                  >
                    {sliderValue}
                  </motion.span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSliderValue((v) => Math.min(PREDICTION_MAX, v + 1));
                    }}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold transition-all active:scale-90"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.6)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    +
                  </button>
                </div>
                <p className="text-xs text-white/40 mt-1">runs</p>
              </div>
              <div className="px-2 mb-5 mt-6">
                <Slider
                  value={[sliderValue]}
                  onValueChange={(v) => setSliderValue(v[0])}
                  min={PREDICTION_MIN}
                  max={PREDICTION_MAX}
                  step={1}
                  className="accent-slider"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-white/30">{PREDICTION_MIN}</span>
                  <span className="text-xs text-white/30">{PREDICTION_MAX}</span>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(false);
                    if (localPrediction) {
                      setSliderValue(localPrediction);
                    }
                  }}
                  variant="outline"
                  className="flex-1 font-medium py-3"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitPrediction}
                  disabled={submitting}
                  className="flex-1 font-semibold py-3"
                  style={{
                    background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                    color: "#0f0f0f",
                  }}
                >
                  <Check className="w-4 h-4 mr-2" />
                  {submitting
                    ? "Saving..."
                    : localPrediction
                      ? "Update Prediction"
                      : "Lock Prediction"}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassCard>
    </motion.div>
  );
};

export default MatchCard;
